import {
  useState,
  useEffect,
  createContext,
  useContext,
  useRef,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import { detectCountry, getCountryName, startAppStateMonitoring, stopAppStateMonitoring } from '@/lib/countryDetection';
import { warmCriticalConnections } from '@/utils/connectionWarming';
import { languageCacheService } from '@/services/languageCacheService';
import { persistentCountryCache } from '@/services/persistentCountryCacheService';
import { unifiedStartupCache } from '@/services/unifiedStartupCache';
import { createScopedLogger } from '@/utils/logging/createScopedLogger';

const logger = createScopedLogger('CountryProvider');
const logWithTimestamp = (msg: string, ...args: unknown[]) => logger.info(msg, ...args);
const errorWithTimestamp = (msg: string, ...args: unknown[]) => logger.error(msg, ...args);
const warnWithTimestamp = (msg: string, ...args: unknown[]) => logger.warn(msg, ...args);

interface CountryState {
  countryCode: string | null;
  countryName: string | null;
  isLoading: boolean;
  error: string | null;
}

interface CountryContextType extends CountryState {
  refetch: () => Promise<void>;
}

const CountryContext = createContext<CountryContextType | undefined>(undefined);

export const useCountry = () => {
  const context = useContext(CountryContext);
  if (!context) {
    throw new Error('useCountry must be used within a CountryProvider');
  }
  return context;
};

export const CountryProvider = ({ children }: { children: ReactNode }) => {
  const isMountedRef = useRef(true);
  const [state, setState] = useState<CountryState>({
    countryCode: null,
    countryName: null,
    isLoading: true,
    error: null,
  });

  const detectCountryCode = useCallback(async () => {
    try {
      logWithTimestamp('🌍 CountryProvider: Starting optimized country detection...');
      setState(prev => ({ ...prev, isLoading: true, error: null }));

      // OPTIMIZATION: Try unified startup cache first (faster, no validation needed)
      const startupCache = await unifiedStartupCache.get();
      if (startupCache && startupCache.countryCode && isMountedRef.current) {
        const cacheAge = unifiedStartupCache.getCacheAge();
        logWithTimestamp(`🌍 CountryProvider: ✅ Using unified cache: ${startupCache.countryCode} (age: ${Math.round((cacheAge || 0) / 60000)}min)`);

        setState({
          countryCode: startupCache.countryCode,
          countryName: startupCache.countryName,
          isLoading: false,
          error: null,
        });

        // Defer background services by 1 second to prioritize UI rendering
        setTimeout(() => {
          languageCacheService.initializeCache().catch((error) => {
            errorWithTimestamp('[LanguageCache] Failed to initialize cache:', error);
          });
        }, 1000);

        // Start connection warming after 500ms (truly fire-and-forget, non-blocking)
        setTimeout(() => {
          warmCriticalConnections().catch((error) => {
            warnWithTimestamp('[Connection Warming] Failed to warm connections:', error);
          });
        }, 500);

        return;
      }

      // Fallback to persistent cache if unified cache not available
      const cached = await persistentCountryCache.get();
      if (cached && isMountedRef.current) {
        logWithTimestamp(`🌍 CountryProvider: ✅ Using persistent cache: ${cached.countryCode} (age: ${Math.round((Date.now() - cached.timestamp) / 1000 / 60)}min)`);

        setState({
          countryCode: cached.countryCode,
          countryName: cached.countryName,
          isLoading: false,
          error: null,
        });

        // Update unified cache in background
        unifiedStartupCache.updateCountryData(cached.countryCode, cached.countryName, 'geoip').catch(err => {
          errorWithTimestamp('Failed to update unified cache:', err);
        });

        // Defer background services - language cache after 1s, connection warming after 500ms
        setTimeout(() => {
          languageCacheService.initializeCache().catch((error) => {
            errorWithTimestamp('[LanguageCache] Failed to initialize cache:', error);
          });
        }, 1000);
        setTimeout(() => {
          warmCriticalConnections().catch((error) => {
            warnWithTimestamp('[Connection Warming] Failed to warm connections:', error);
          });
        }, 500);

        return;
      }

      // No cache - detect fresh (only happens on first launch)
      logWithTimestamp('🌍 CountryProvider: No cache, detecting country...');
      const detectedCountryCode = await detectCountry();

      if (isMountedRef.current) {
        const detectedCountryName = getCountryName(detectedCountryCode);
        logWithTimestamp(`🌍 CountryProvider: Fresh detection complete: ${detectedCountryCode}`);

        // Save to both caches (unified cache first for faster startup next time)
        await Promise.all([
          unifiedStartupCache.updateCountryData(detectedCountryCode, detectedCountryName, 'geoip'),
          persistentCountryCache.set(detectedCountryCode, detectedCountryName, 'geoip')
        ]);

        setState({
          countryCode: detectedCountryCode,
          countryName: detectedCountryName,
          isLoading: false,
          error: null,
        });

        logWithTimestamp(`🌍 CountryProvider: ✅ Country set and cached: ${detectedCountryCode}`);

        // Defer background services - language cache after 1s, connection warming after 500ms
        setTimeout(() => {
          languageCacheService.initializeCache().catch((error) => {
            errorWithTimestamp('[LanguageCache] Failed to initialize cache:', error);
          });
        }, 1000);
        setTimeout(() => {
          warmCriticalConnections().catch((error) => {
            warnWithTimestamp('[Connection Warming] Failed to warm connections:', error);
          });
        }, 500);
      }
    } catch (err) {
      errorWithTimestamp('🌍 CountryProvider: Error detecting country:', err);
      if (isMountedRef.current) {
        logWithTimestamp('🌍 CountryProvider: Using US fallback...');

        // Save fallback to both caches
        await Promise.all([
          unifiedStartupCache.updateCountryData('US', 'United States', 'fallback'),
          persistentCountryCache.set('US', 'United States', 'fallback')
        ]);

        setState({
          countryCode: 'US',
          countryName: 'United States',
          isLoading: false,
          error: err instanceof Error ? err.message : 'Failed to detect country',
        });

        logWithTimestamp('🌍 CountryProvider: ✅ US fallback set');

        // Defer background services - language cache after 1s, connection warming after 500ms
        setTimeout(() => {
          languageCacheService.initializeCache().catch((error) => {
            errorWithTimestamp('[LanguageCache] Failed to initialize cache:', error);
          });
        }, 1000);
        setTimeout(() => {
          warmCriticalConnections().catch((error) => {
            warnWithTimestamp('[Connection Warming] Failed to warm connections:', error);
          });
        }, 500);
      }
    } finally {
      logWithTimestamp('🌍 CountryProvider: Country detection complete');
    }
  }, []);

  useEffect(() => {
    isMountedRef.current = true;

    // Start monitoring app state for cache clearing on background
    startAppStateMonitoring();

    // Start country detection
    detectCountryCode();

    return () => {
      isMountedRef.current = false;
      // Clean up app state monitoring
      stopAppStateMonitoring();
    };
  }, [detectCountryCode]);

  const refetch = useCallback(async () => {
    await detectCountryCode();
  }, [detectCountryCode]);

  const value = useMemo(
    () => ({
      ...state,
      refetch,
    }),
    [state, refetch],
  );

  return (
    <CountryContext.Provider value={value}>
      {children}
    </CountryContext.Provider>
  );
};